// @ts-check
// Estatísticas do fluxo (/campaigns/flows/:flowId/stats; spec 2.0.0, fluxos.md seções 20.13 e 20.14; contrato 09 de
// fluxos, seção 17): cabeçalho com "Editar fluxo" e o menu, "Mais informações", o período e o agrupamento na URL, os
// números (cada um abre a lista dos cards quando faz sentido) e as abas Visão geral (gráfico e o caminho do fluxo com
// os contadores), E-mail, WhatsApp, Saídas e falhas e Atividades.
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { downloadFlowsReport, duplicateFlow, getFlow, getFlowNodeCounts, getFlowStats, retryFlowRuns } from '../../data/flows.js';
import { ApiError } from '../../data/api-client.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { TabList } from '../../ui/tab-list.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { downloadBlob } from '../../lib/csv.js';
import { formatDateTime, formatNumber, getFormatPreferences } from '../../lib/format.js';
import { FLOW_STATS_PRESETS, flowEditorHref, flowStatsRange, readFlowStatsPeriod } from '../../lib/flow-list.js';
import { walkFlowNodes } from '../../../../shared/flows/flow-definition.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { FlowCountLink, FlowStatusChip, describeNode, flowReasonText } from './flow-ui.js';
import { FlowMenuButton } from './flow-menu.js';
import { FlowMiniCanvas, miniCanvasLabel } from './flow-mini-canvas.js';
import { FlowRunsList } from './flow-runs-list.js';
import { FlowDeactivateDialog } from './flow-lifecycle-dialogs.js';
import { openFlowInsertLeadsDialog } from './flow-insert-leads-dialog.js';
import { describeTrigger } from './flow-entry-card.js';
import { stageOptions, useFlowLookupData, useSummaryLookups } from './flow-lookups.js';
import { exitSummaryText } from './flow-activation-modal.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/flows.js').FlowDetail} FlowDetail
 * @typedef {import('../../data/flows.js').FlowNodeCounts} FlowNodeCounts
 */

const STATS_TABS = Object.freeze(['overview', 'email', 'whatsapp', 'exits', 'runs']);
const GROUPS = Object.freeze(['day', 'week', 'month']);

/** Taxa em porcentagem, uma casa. @param {number} value */
const percent = (value) => formatNumber(Math.round(value * 1000) / 10, { decimals: 1 }) + '%';

/**
 * Números do período no formato do canvas: passaram, saíram e os ramos do período, e o "agora" ao vivo.
 * @param {any} stats @param {FlowNodeCounts | null} live
 * @returns {FlowNodeCounts | null}
 */
export function periodCanvasCounts(stats, live) {
  if (!stats) return live;
  /** @type {Record<string, any>} */
  const nodes = {};
  for (const [nodeId, value] of Object.entries(stats.nodes ?? {})) {
    const current = live?.nodes?.[nodeId];
    nodes[nodeId] = {
      now: current?.now ?? 0, failedNow: current?.failedNow ?? 0, arrived: Number(value.arrived ?? 0), passed: Number(value.passed ?? 0),
      skipped: Number(value.skipped ?? 0), failed: Number(value.failed ?? 0), exited: Number(value.exited ?? 0), branches: value.branches ?? null,
    };
  }
  return {
    computedAt: live?.computedAt ?? new Date().toISOString(),
    entry: {
      entered: Number(stats.totals?.entered ?? 0), inFlow: Number(stats.totals?.inFlowNow ?? live?.entry?.inFlow ?? 0),
      completed: Number(stats.totals?.completed ?? 0), exited: Number(stats.totals?.exited ?? 0), stopped: Number(stats.totals?.stopped ?? 0),
    },
    nodes,
  };
}

/** @param {{ flowId: string }} props */
export function FlowStatsPage({ flowId }) {
  const parameters = searchParamsState.value;
  const timeZone = getFormatPreferences().timeZone;
  const period = readFlowStatsPeriod(parameters, new Date(), timeZone);
  const activeTab = STATS_TABS.includes(parameters.get('view') ?? '') ? /** @type {string} */ (parameters.get('view')) : 'overview';
  const [flow, setFlow] = useState(/** @type {FlowDetail | null} */ (null));
  const [loadState, setLoadState] = useState(/** @type {'loading' | 'ready' | 'error' | 'notFound'} */ ('loading'));
  const [stats, setStats] = useState(/** @type {any} */ (null));
  const [statsError, setStatsError] = useState(false);
  const [live, setLive] = useState(/** @type {FlowNodeCounts | null} */ (null));
  const [showInfo, setShowInfo] = useState(false);
  const [custom, setCustom] = useState({ from: period.from, to: period.to });
  const [isCustomOpen, setIsCustomOpen] = useState(period.preset === 'CUSTOM');
  const [dialog, setDialog] = useState(/** @type {'deactivate' | null} */ (null));
  const lookupData = useFlowLookupData({ includeFlows: true });
  const lookups = useSummaryLookups(lookupData, flow?.definition ?? null);

  const loadFlow = () => {
    setLoadState('loading');
    getFlow(flowId).then((loaded) => {
      setFlow(loaded);
      setLoadState('ready');
      document.title = `${loaded.name} · ${t('flows.stats.title')}`;
    }).catch((error) => {
      if (error instanceof ApiError && (error.statusCode === 404 || error.code === 'RECORD_NOT_FOUND')) setLoadState('notFound');
      else setLoadState('error');
    });
  };
  useEffect(loadFlow, [flowId]);
  const wasActivated = Boolean(flow && (flow.firstActivatedAt || flow.activatedAt || flow.hasRuns || flow.status !== 'DRAFT'));
  const loadStats = () => {
    if (!wasActivated) return;
    setStatsError(false);
    setStats(null);
    getFlowStats(flowId, { from: period.from, to: period.to, groupBy: period.groupBy }).then(setStats).catch(() => setStatsError(true));
    getFlowNodeCounts(flowId).then(setLive).catch(() => undefined);
  };
  useEffect(loadStats, [flowId, wasActivated, period.from, period.to, period.groupBy]);

  const canvasCounts = useMemo(() => periodCanvasCounts(stats, live), [stats, live]);

  if (loadState === 'notFound') {
    return html`<${PageCard} className="flows-page" header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.stats.title')} />`}>
      <div class="flows-state"><${EmptyState} icon="IconSitemap" title=${t('flows.editor.notFound')}
        action=${html`<${Button} title=${t('flows.editor.backToList')} variant="secondary" size="small" onClick=${() => navigate('/campaigns/flows')} />`} /></div>
    <//>`;
  }
  if (loadState === 'error') {
    return html`<${PageCard} className="flows-page" header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.stats.title')} />`}>
      <div class="flows-state"><${EmptyState} icon="IconAlertTriangle" title=${t('flows.stats.loadError')}
        action=${html`<${Button} title=${t('flows.common.retry')} variant="secondary" size="small" onClick=${loadFlow} />`} /></div>
    <//>`;
  }
  if (!flow) {
    return html`<${PageCard} className="flows-page" header=${html`<${PageHeader} icon="IconSitemap" title=${t('flows.stats.title')} />`}>
      <div class="flow-stats" aria-busy="true"><${Skeleton} height=${32} width="40%" /><div class="flow-stats-numbers">${[0, 1, 2, 3, 4, 5].map((key) => html`<${Skeleton} key=${key} height=${88} radius=${8} />`)}</div></div>
    <//>`;
  }

  const definition = flow.definition;
  /** @type {Array<{ id: string, title: string, type: string }>} */
  const nodeChoices = [];
  walkFlowNodes(definition.nodes, (node) => nodeChoices.push({ id: node.id, type: node.type, title: describeNode(node, lookups).title }));
  const periodForLinks = { from: period.from, to: period.to };
  /** @param {Record<string, string | null>} changes */
  const setParams = (changes) => updateSearchParams(changes);
  const menuItems = [
    { key: 'duplicate', text: t('flows.list.menu.duplicate'), icon: 'IconCopy', onSelect: async () => {
      try {
        const copy = await duplicateFlow(flow.id);
        navigate(flowEditorHref(copy.id));
      } catch (error) {
        showErrorSnackbar(error);
      }
    } },
    ...(flow.status === 'ACTIVE' ? [{ key: 'insert', text: t('flows.list.menu.insert'), icon: 'IconUserPlus', onSelect: () => openFlowInsertLeadsDialog({ flow: { id: flow.id, name: flow.name }, onDone: loadStats }) }] : []),
    flow.status === 'ACTIVE'
      ? { key: 'deactivate', text: t('flows.list.menu.deactivate'), icon: 'IconPlayerPause', onSelect: () => setDialog('deactivate') }
      : { key: 'activate', text: t('flows.list.menu.activate'), icon: 'IconPlayerPlay', onSelect: () => navigate(flowEditorHref(flow.id, { activate: true })) },
    { key: 'export', text: t('flows.stats.exportCsv'), icon: 'IconFileExport', onSelect: async () => {
      try {
        const file = await downloadFlowsReport({ from: period.from, to: period.to });
        downloadBlob(file.blob, file.fileName ?? `fluxos-${period.from}-a-${period.to}.csv`);
      } catch (error) {
        showErrorSnackbar(error);
      }
    } },
  ];
  const optionLabel = (/** @type {string} */ fieldName, /** @type {string} */ value) => lookups.optionLabel?.(fieldName, value) ?? null;
  const entryText = definition.entry.mode === 'MANUAL_ONLY' ? t('flows.entry.manualText')
    : definition.entry.triggers.map((trigger) => {
      const described = describeTrigger(trigger, lookups, optionLabel);
      return `${described.lead} ${described.values.join(', ')}`;
    }).join(` ${t('flows.entry.or')} `);

  const header = html`<${PageHeader} icon="IconChartBar" title=${flow.name}
    breadcrumb=${html`<button type="button" class="flow-back-link" onClick=${() => navigate('/campaigns/flows')}><${Icon} name="IconChevronLeft" size=${16} /><span>${t('flows.list.title')}</span></button>`}
    actions=${html`<${FlowStatusChip} status=${flow.status} inactiveReason=${flow.inactiveReason} inactiveDetails=${flow.inactiveDetails ?? null} />
      <div class="flow-split-button">
        <${Button} title=${t('flows.stats.editFlow')} icon="IconPencil" accent="blue" size="small" onClick=${() => navigate(flowEditorHref(flow.id))} />
        <${FlowMenuButton} ariaLabel=${t('flows.stats.moreActions')} icon="IconChevronDown" items=${menuItems} className="flow-menu-trigger flow-split-trigger" />
      </div>`} />`;

  if (!wasActivated) {
    return html`<${PageCard} className="flows-page flow-stats-page" header=${header}>
      <div class="flows-state" data-testid="flow-stats-not-activated"><${EmptyState} icon="IconChartBar" title=${t('flows.stats.notActivated')}
        action=${html`<${Button} title=${t('flows.stats.openEditor')} accent="blue" size="small" onClick=${() => navigate(flowEditorHref(flow.id))} />`} /></div>
    <//>`;
  }

  const totals = stats?.totals ?? null;
  const numberCards = [
    { key: 'entered', label: t('flows.stats.numbers.entered'), value: totals?.entered, metric: 'ENTERED', icon: 'IconDoorEnter' },
    { key: 'inFlow', label: t('flows.stats.numbers.inFlow'), value: totals?.inFlowNow, metric: 'IN_FLOW', icon: 'IconHourglass' },
    { key: 'completed', label: t('flows.stats.numbers.completed'), value: totals?.completed, metric: 'COMPLETED', icon: 'IconCircleCheck' },
    { key: 'exited', label: t('flows.stats.numbers.exited'), value: totals?.exited, metric: null, icon: 'IconLogout' },
    { key: 'visited', label: t('flows.stats.numbers.visited'), value: totals?.visitDoneAfterEntry, metric: null, icon: 'IconCalendarEvent' },
    { key: 'enrolled', label: t('flows.stats.numbers.enrolled'), value: totals?.enrolledAfterEntry, metric: null, icon: 'IconSchool' },
  ];
  const hasData = Boolean(stats && (Number(totals?.entered ?? 0) > 0 || Number(totals?.inFlowNow ?? 0) > 0 || (stats.series ?? []).some((item) => item.entered > 0)));

  return html`<${PageCard} className="flows-page flow-stats-page" header=${header}>
    <div class="flow-stats" data-testid="flow-stats-page">
      <div class="flow-stats-info">
        <button type="button" class="flow-link-button" aria-expanded=${showInfo ? 'true' : 'false'} onClick=${() => setShowInfo(!showInfo)}>
          <${Icon} name=${showInfo ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />${t('flows.stats.moreInfo')}
        </button>
        ${showInfo
          ? html`<dl class="flow-stats-info-list">
              <dt>${t('flows.settings.owner')}</dt><dd>${flow.owner?.name ?? t('flows.settings.noOwner')}</dd>
              <dt>${t('flows.stats.activatedAt')}</dt><dd>${flow.activatedAt ? formatDateTime(flow.activatedAt) : '-'}</dd>
              <dt>${t('flows.stats.createdAt')}</dt><dd>${formatDateTime(flow.createdAt)}</dd>
              <dt>${t('flows.stats.revision')}</dt><dd>${flow.revision}</dd>
              <dt>${t('flows.nameDialog.collection')}</dt><dd>${flow.collection ?? '-'}</dd>
              <dt>${t('flows.stats.entry')}</dt><dd>${entryText || t('flows.entry.notSet')}</dd>
              <dt>${t('flows.stats.exit')}</dt><dd>${exitSummaryText(definition, (value) => stageOptions().find((option) => option.value === value)?.label ?? value)}</dd>
            </dl>`
          : null}
      </div>
      <div class="flow-stats-period">
        <${SegmentedControl} label=${t('flows.stats.period')} value=${isCustomOpen ? 'CUSTOM' : period.preset}
          options=${FLOW_STATS_PRESETS.map((preset) => ({ value: preset, label: t(`flows.stats.presets.${preset}`) }))}
          onChange=${(/** @type {string} */ preset) => {
            if (preset === 'CUSTOM') {
              setIsCustomOpen(true);
              return;
            }
            setIsCustomOpen(false);
            const range = flowStatsRange(preset, new Date(), timeZone);
            setParams({ from: range.from, to: range.to, period: null });
          }} />
        ${isCustomOpen
          ? html`<form class="flow-stats-custom" onSubmit=${(/** @type {Event} */ event) => {
              event.preventDefault();
              if (custom.from && custom.to && custom.from <= custom.to) setParams({ from: custom.from, to: custom.to, period: null });
            }}>
              <input type="date" class="text-input" value=${custom.from} aria-label=${t('flows.stats.from')}
                onInput=${(/** @type {InputEvent} */ event) => setCustom({ ...custom, from: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
              <input type="date" class="text-input" value=${custom.to} aria-label=${t('flows.stats.to')}
                onInput=${(/** @type {InputEvent} */ event) => setCustom({ ...custom, to: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
              <${Button} title=${t('flows.stats.apply')} variant="secondary" size="small" type="submit" disabled=${!custom.from || !custom.to || custom.from > custom.to} />
            </form>`
          : null}
        <label class="flow-inline-field">
          <span>${t('flows.stats.groupBy')}</span>
          <select class="text-input" value=${period.groupBy} onChange=${(/** @type {Event} */ event) => setParams({ groupBy: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
            ${GROUPS.map((group) => html`<option key=${group} value=${group}>${t(`flows.stats.groups.${group}`)}</option>`)}
          </select>
        </label>
      </div>
      ${statsError
        ? html`<${EmptyState} icon="IconAlertTriangle" title=${t('flows.stats.loadError')}
            action=${html`<${Button} title=${t('flows.common.retry')} variant="secondary" size="small" onClick=${loadStats} />`} />`
        : html`<div class="flow-stats-numbers">
            ${numberCards.map((card) => html`<div key=${card.key} class="flow-stats-number" data-testid=${`flow-stats-${card.key}`}>
              <span class="flow-stats-number-label"><${Icon} name=${card.icon} size=${14} />${card.label}</span>
              ${stats === null
                ? html`<${Skeleton} width=${64} height=${28} />`
                : card.metric
                  ? html`<${FlowCountLink} flowId=${flow.id} nodeKey="entry" metric=${card.metric} value=${Number(card.value ?? 0)} className="flow-stats-number-value"
                      period=${card.metric === 'IN_FLOW' ? {} : periodForLinks} label=${`${card.label}: ${formatNumber(Number(card.value ?? 0))}`} />`
                  : html`<span class="flow-stats-number-value" data-empty="true">${formatNumber(Number(card.value ?? 0))}</span>`}
            </div>`)}
          </div>`}
      <${TabList} activeTabId=${activeTab} onChange=${(/** @type {string} */ tab) => setParams({ view: tab === 'overview' ? null : tab })}
        tabs=${STATS_TABS.map((tab) => ({ id: tab, title: t(`flows.stats.tabs.${tab}`) }))} />
      <div class="flow-stats-tab" role="tabpanel" aria-labelledby=${`tab-${activeTab}`}>
        ${stats && !hasData && activeTab !== 'runs' ? html`<p class="flow-stats-empty">${t('flows.stats.noData')}</p>` : null}
        ${activeTab === 'overview'
          ? html`<${FlowStatsChart} series=${stats?.series ?? null} groupBy=${period.groupBy} />
            <h3 class="flow-stats-section-title">${t('flows.stats.path')}</h3>
            <div class="flow-stats-canvas">
              <${FlowMiniCanvas} definition=${definition} lookups=${lookups} ariaLabel=${miniCanvasLabel(flow.name)} initialZoom=${0.8} counts=${canvasCounts}
                flowId=${flow.id} period=${periodForLinks} />
            </div>`
          : null}
        ${activeTab === 'email' ? html`<${FlowEmailTable} stats=${stats} nodes=${nodeChoices.filter((node) => node.type === 'SEND_EMAIL')} flowId=${flow.id} period=${periodForLinks} />` : null}
        ${activeTab === 'whatsapp' ? html`<${FlowWhatsappTable} stats=${stats} nodes=${nodeChoices.filter((node) => node.type === 'SEND_WHATSAPP')} flowId=${flow.id} period=${periodForLinks} />` : null}
        ${activeTab === 'exits' ? html`<${FlowExitsTab} stats=${stats} live=${live} nodes=${nodeChoices} flowId=${flow.id} onRetried=${loadStats} />` : null}
        ${activeTab === 'runs' ? html`<${FlowRunsList} flowId=${flow.id} nodes=${nodeChoices.map((node) => ({ id: node.id, title: node.title }))} />` : null}
      </div>
    </div>
    ${dialog === 'deactivate'
      ? html`<${FlowDeactivateDialog} flows=${[{ id: flow.id, name: flow.name, status: flow.status, inFlow: live?.entry?.inFlow ?? null }]} onClose=${() => setDialog(null)}
          onDone=${(/** @type {FlowDetail[]} */ updated) => {
            setDialog(null);
            if (updated[0]) setFlow(updated[0]);
          }} />`
      : null}
  <//>`;
}

/**
 * Entradas e conclusões por período: barras lado a lado, com a tabela para leitor de tela.
 * @param {{ series: Array<{ bucket: string, entered: number, completed: number }> | null, groupBy: string }} props
 */
function FlowStatsChart({ series, groupBy }) {
  if (series === null) return html`<${Skeleton} height=${180} radius=${8} />`;
  const maximum = Math.max(1, ...series.map((item) => Math.max(item.entered, item.completed)));
  /** @param {string} bucket */
  const label = (bucket) => {
    const [year, month, day] = bucket.split('-');
    return groupBy === 'month' ? `${month}/${year.slice(2)}` : `${day}/${month}`;
  };
  return html`<figure class="flow-stats-chart" aria-label=${t('flows.stats.chartLabel')}>
    <div class="flow-stats-legend" aria-hidden="true">
      <span data-series="entered">${t('flows.stats.numbers.entered')}</span>
      <span data-series="completed">${t('flows.stats.numbers.completed')}</span>
    </div>
    <div class="flow-stats-bars" aria-hidden="true">
      ${series.map((item) => html`<div key=${item.bucket} class="flow-stats-bar-group" title=${`${label(item.bucket)}: ${formatNumber(item.entered)} / ${formatNumber(item.completed)}`}>
        <div class="flow-stats-bar-pair">
          <span class="flow-stats-bar" data-series="entered" style=${{ height: `${(item.entered / maximum) * 100}%` }}></span>
          <span class="flow-stats-bar" data-series="completed" style=${{ height: `${(item.completed / maximum) * 100}%` }}></span>
        </div>
        <span class="flow-stats-bar-label">${label(item.bucket)}</span>
      </div>`)}
    </div>
    <table class="visually-hidden-text">
      <caption>${t('flows.stats.chartLabel')}</caption>
      <thead><tr><th scope="col">${t('flows.stats.periodColumn')}</th><th scope="col">${t('flows.stats.numbers.entered')}</th><th scope="col">${t('flows.stats.numbers.completed')}</th></tr></thead>
      <tbody>${series.map((item) => html`<tr key=${item.bucket}><td>${label(item.bucket)}</td><td>${item.entered}</td><td>${item.completed}</td></tr>`)}</tbody>
    </table>
  </figure>`;
}

/**
 * Tabela dos e-mails do fluxo no período.
 * @param {{ stats: any, nodes: Array<{ id: string, title: string }>, flowId: string, period: { from: string, to: string } }} props
 */
function FlowEmailTable({ stats, nodes, flowId, period }) {
  if (nodes.length === 0) return html`<p class="flow-stats-empty">${t('flows.stats.noEmails')}</p>`;
  if (!stats) return html`<${Skeleton} height=${120} />`;
  return html`<div class="flow-stats-table-wrapper"><table class="flow-stats-table">
    <thead><tr>
      <th scope="col">${t('flows.stats.columns.email')}</th><th scope="col">${t('flows.stats.columns.sent')}</th><th scope="col">${t('flows.stats.columns.delivered')}</th>
      <th scope="col">${t('flows.stats.columns.opened')}</th><th scope="col">${t('flows.stats.columns.clicked')}</th>
      <th scope="col">${t('flows.stats.columns.unsubscribed')}</th><th scope="col">${t('flows.stats.columns.bounced')}</th>
    </tr></thead>
    <tbody>${nodes.map((node) => {
      const row = stats.email?.[node.id] ?? {};
      return html`<tr key=${node.id}>
        <th scope="row"><${FlowCountLink} flowId=${flowId} nodeKey=${node.id} metric="PASSED" value=${Number(row.sent ?? 0)} period=${period} className="flow-stats-link"
          label=${t('flows.stats.openList', { name: node.title })}><span>${node.title}</span><//></th>
        <td>${formatNumber(Number(row.sent ?? 0))}</td><td>${formatNumber(Number(row.delivered ?? 0))}</td>
        <td>${formatNumber(Number(row.opened ?? 0))} <span class="flow-stats-rate">${percent(Number(row.rates?.openRate ?? 0))}</span></td>
        <td>${formatNumber(Number(row.clicked ?? 0))} <span class="flow-stats-rate">${percent(Number(row.rates?.clickRate ?? 0))}</span></td>
        <td>${formatNumber(Number(row.unsubscribed ?? 0))}</td><td>${formatNumber(Number(row.bounced ?? 0))}</td>
      </tr>`;
    })}</tbody>
  </table></div>
  <p class="flow-field-hint">${t('flows.forms.engagement.estimateNotice')}</p>`;
}

/**
 * Tabela dos WhatsApps do fluxo no período.
 * @param {{ stats: any, nodes: Array<{ id: string, title: string }>, flowId: string, period: { from: string, to: string } }} props
 */
function FlowWhatsappTable({ stats, nodes, flowId, period }) {
  if (nodes.length === 0) return html`<p class="flow-stats-empty">${t('flows.stats.noWhatsapp')}</p>`;
  if (!stats) return html`<${Skeleton} height=${120} />`;
  return html`<div class="flow-stats-table-wrapper"><table class="flow-stats-table">
    <thead><tr>
      <th scope="col">${t('flows.stats.columns.message')}</th><th scope="col">${t('flows.stats.columns.sent')}</th><th scope="col">${t('flows.stats.columns.delivered')}</th>
      <th scope="col">${t('flows.stats.columns.read')}</th><th scope="col">${t('flows.stats.columns.failed')}</th>
    </tr></thead>
    <tbody>${nodes.map((node) => {
      const row = stats.whatsapp?.[node.id] ?? {};
      const sent = Number(row.sent ?? 0);
      const delivered = Number(row.delivered ?? 0);
      const reasons = Object.entries(row.failedByReason ?? {}).map(([code, count]) => t('flows.stats.whatsappFailure', { code, count: formatNumber(Number(count)) })).join('; ');
      return html`<tr key=${node.id}>
        <th scope="row"><${FlowCountLink} flowId=${flowId} nodeKey=${node.id} metric="PASSED" value=${sent} period=${period} className="flow-stats-link"
          label=${t('flows.stats.openList', { name: node.title })}><span>${node.title}</span><//></th>
        <td>${formatNumber(sent)}</td>
        <td>${formatNumber(delivered)} <span class="flow-stats-rate">${percent(sent ? delivered / sent : 0)}</span></td>
        <td>${formatNumber(Number(row.read ?? 0))} <span class="flow-stats-rate">${percent(delivered ? Number(row.read ?? 0) / delivered : 0)}</span></td>
        <td title=${reasons || undefined}>${formatNumber(Number(row.deliveryFailed ?? 0))}${reasons ? html` <${Icon} name="IconInfoCircle" size=${12} />` : null}</td>
      </tr>`;
    })}</tbody>
  </table></div>`;
}

/**
 * Saídas por motivo, entradas e passos ignorados por motivo e as falhas por passo com "Tentar de novo".
 * @param {{ stats: any, live: FlowNodeCounts | null, nodes: Array<{ id: string, title: string }>, flowId: string, onRetried: () => void }} props
 */
function FlowExitsTab({ stats, live, nodes, flowId, onRetried }) {
  const [busyNodeId, setBusyNodeId] = useState(/** @type {string | null} */ (null));
  if (!stats) return html`<${Skeleton} height=${120} />`;
  /** @type {Record<string, number>} */
  const stepSkips = {};
  for (const value of Object.values(stats.nodes ?? {})) {
    for (const [reason, count] of Object.entries(/** @type {any} */ (value).skipsByReason ?? {})) stepSkips[reason] = (stepSkips[reason] ?? 0) + Number(count);
  }
  /** @param {string} title @param {Record<string, number>} values @param {'EXIT' | 'ENTRY_SKIP' | 'STEP_SKIP'} kind */
  const reasonList = (title, values, kind) => {
    const entries = Object.entries(values ?? {}).filter(([, count]) => Number(count) > 0).sort((first, second) => Number(second[1]) - Number(first[1]));
    return html`<section class="flow-stats-reasons">
      <h3 class="flow-stats-section-title">${title}</h3>
      ${entries.length === 0
        ? html`<p class="flow-field-hint">${t('flows.stats.noneInPeriod')}</p>`
        : html`<ul>${entries.map(([reason, count]) => html`<li key=${reason}><span>${flowReasonText(kind, reason)}</span><strong>${formatNumber(Number(count))}</strong></li>`)}</ul>`}
    </section>`;
  };
  const failures = nodes.map((node) => ({ ...node, count: Number(live?.nodes?.[node.id]?.failedNow ?? 0) })).filter((node) => node.count > 0);
  /** @param {string} nodeId */
  const retry = async (nodeId) => {
    setBusyNodeId(nodeId);
    try {
      const count = await retryFlowRuns(flowId, { nodeId });
      showSuccessSnackbar(t('flows.runs.retried', { count }));
      onRetried();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyNodeId(null);
    }
  };
  return html`<div class="flow-stats-exits">
    ${reasonList(t('flows.stats.exitsByReason'), stats.exitsByReason, 'EXIT')}
    ${reasonList(t('flows.stats.entrySkips'), stats.entrySkipsByReason, 'ENTRY_SKIP')}
    ${reasonList(t('flows.stats.stepSkips'), stepSkips, 'STEP_SKIP')}
    <section class="flow-stats-reasons">
      <h3 class="flow-stats-section-title">${t('flows.stats.failures')}</h3>
      ${failures.length === 0
        ? html`<p class="flow-field-hint">${t('flows.stats.noFailures')}</p>`
        : html`<ul>${failures.map((node) => html`<li key=${node.id}>
            <span>${node.title}</span>
            <${FlowCountLink} flowId=${flowId} nodeKey=${node.id} metric="FAILED_NOW" value=${node.count} tone="danger" label=${t('flows.counts.failedNow', { count: node.count })} />
            <${Button} title=${t('flows.stats.retryCount', { count: node.count })} variant="secondary" size="small" isLoading=${busyNodeId === node.id} onClick=${() => retry(node.id)} />
          </li>`)}</ul>`}
    </section>
  </div>`;
}
